import { isStringArray, useStored } from './storage';

// Browser-only state from the data model: taste.v1, hiddenRecs.v1, recentSearches.v1.

export type NeverShow = { podcasts: boolean; audiobooks: boolean; aiMusic: boolean; unfollowedNewReleases: boolean };

export type TasteSettings = {
  doc: { name: string; addedAt: string } | null;
  /** Mock extracted artists the user removed. */
  removedDocArtists: string[];
  useOpened: boolean;
  /** Mock "artists you open" entries the user forgot. */
  forgottenOpened: string[];
  /** 0–100: share of the taste doc vs artists you open. */
  mix: number;
  includeSinglesEps: boolean;
  never: NeverShow;
};

export const DEFAULT_TASTE: TasteSettings = {
  doc: null,
  removedDocArtists: [],
  useOpened: true,
  forgottenOpened: [],
  mix: 60,
  includeSinglesEps: false,
  never: { podcasts: true, audiobooks: true, aiMusic: true, unfollowedNewReleases: true },
};

const isTaste = (v: unknown): v is TasteSettings => {
  if (!v || typeof v !== 'object') return false;
  const t = v as TasteSettings;
  return (
    (t.doc === null || (typeof t.doc === 'object' && typeof t.doc.name === 'string')) &&
    isStringArray(t.removedDocArtists) &&
    typeof t.useOpened === 'boolean' &&
    isStringArray(t.forgottenOpened) &&
    typeof t.mix === 'number' &&
    typeof t.includeSinglesEps === 'boolean' &&
    !!t.never &&
    typeof t.never === 'object'
  );
};

export function useTaste() {
  return useStored<TasteSettings>('taste.v1', DEFAULT_TASTE, isTaste);
}

export function useHiddenRecs() {
  return useStored<string[]>('hiddenRecs.v1', [], isStringArray);
}

export type RecentArtist = { id: string; name: string };
const isRecent = (v: unknown): v is RecentArtist[] =>
  Array.isArray(v) && v.every((x) => x && typeof x.id === 'string' && typeof x.name === 'string');

export const RECENT_MAX = 10;

/** Recent searches are the artists opened from search, newest first, deduped, max 10. */
export function useRecentSearches() {
  const [recent, setRecent] = useStored<RecentArtist[]>('recentSearches.v1', [], isRecent);
  return {
    recent,
    push: (a: RecentArtist) => setRecent((cur) => [a, ...cur.filter((x) => x.id !== a.id)].slice(0, RECENT_MAX)),
    remove: (id: string) => setRecent((cur) => cur.filter((x) => x.id !== id)),
    clear: () => setRecent([]),
  };
}
